import { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWandMagicSparkles,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin } from "../../types";
import { Accordion } from "../../Accordion";

const TAG_COLORS = [
  "#1a6b3f",
  "#6b3fa0",
  "#2563eb",
  "#b45309",
  "#be185d",
  "#0e7490",
];

interface WarpPreset {
  id: string;
  label: string;
  vertical: string;
  tags: string[];
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || ""
  );
}

function WarpToStepPanel() {
  const [presets, setPresets] = useState<Record<string, WarpPreset[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/dev_tools/journey/warp_targets")
      .then((res) => res.json())
      .then((data) => {
        const grouped: Record<string, WarpPreset[]> = {};
        for (const preset of data.presets || []) {
          const vertical = preset.vertical || "Other";
          if (!grouped[vertical]) grouped[vertical] = [];
          grouped[vertical].push(preset);
        }
        setPresets(grouped);
      })
      .catch(() => setError("Failed to load warp targets"));
  }, []);

  const handleWarp = useCallback((presetId: string) => {
    setLoading(true);
    setError(null);

    fetch("/dev_tools/journey/warp_to_step", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({ preset_id: presetId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.url) {
          window.location.href = data.url;
        } else {
          setError(data.error || "Unknown error");
          setLoading(false);
        }
      })
      .catch((err) => {
        setError(`Request failed: ${err.message}`);
        setLoading(false);
      });
  }, []);

  if (error) {
    return (
      <div className="dt-status-banner dt-status-banner--warning">
        <div className="dt-status-banner__title">Error</div>
        <div className="dt-status-banner__text">{error}</div>
      </div>
    );
  }

  const groups = Object.entries(presets);

  if (groups.length === 0) {
    return (
      <div className="dt-skip__description">Loading warp targets...</div>
    );
  }

  return (
    <>
      <div className="dt-skip__description">
        Jump directly to a journey step with pre-populated data, bypassing all
        preceding steps.
      </div>

      {groups.map(([vertical, items]) => (
        <Accordion key={vertical} title={vertical.toUpperCase()} count={items.length}>
          <div className="dt-chip-grid">
            {items.map((preset) => (
              <button
                key={preset.id}
                className="dt-chip"
                disabled={loading}
                onClick={() => handleWarp(preset.id)}
                type="button"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "4px",
                }}
              >
                <span>{preset.label}</span>
                {preset.tags.length > 0 && (
                  <span style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {preset.tags.map((tag, i) => (
                      <span
                        key={tag}
                        style={{
                          background: TAG_COLORS[i % TAG_COLORS.length],
                          borderRadius: "3px",
                          padding: "1px 6px",
                          fontSize: "0.8em",
                          color: "#fff",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Accordion>
      ))}

      {loading && (
        <div
          className="dt-autofill__overlay"
          aria-busy="true"
          aria-live="polite"
        >
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="dt-autofill__spinner"
          />
        </div>
      )}
    </>
  );
}

export function warpToStepPlugin(): DevToolsPlugin {
  return {
    id: "warp-to-step",
    name: "Warp to Step",
    icon: faWandMagicSparkles,
    render: () => <WarpToStepPanel />,
  };
}
